import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import { IconX } from '@tabler/icons-react'

import {
  selectHierarchyOrganization,
  selectHierarchyOrganizations,
  setFilter,
} from '@/reducers/analytics/analytics.redux'
import { ReactSelectChevron } from '@/components/icons/ReactSelectChevron'
import {
  getAllNodeIDs,
  getNodeWithChildrenIDs,
  findNodeByValue,
} from '@/components/filters/filter.helpers'
import { filterHierarchy } from '@/utils/helpers'
import { TreeSelect } from '@/components/forms/TreeSelect'
import './OrganizationsFilter.scss'

export const OrganizationsFilter = ({ ...props }) => {
  const dispatch = useDispatch()
  const [controlsVisible, setControlsVisible] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const {
    options: optionsDB,
    disabled = false,
    accessor = 'organization',
    initialSelection,
    disabledIDs,
    dispatchOnClose = false,
  } = props
  const [allIDs, setAllIDs] = useState([])
  const [selectedIDs, setSelectedIDs] = useState(initialSelection ?? [])
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const containerRef = useRef(null)

  const dispatchSelections = useCallback(
    (currentSelectedIDs) => {
      const newSelectionCount = currentSelectedIDs.length
      const allSelected = newSelectionCount === 0 || newSelectionCount === allIDs.length
      const organizations = allSelected ? allIDs : currentSelectedIDs
      const shouldResetFilters =
        newSelectionCount === 0 ||
        newSelectionCount > 1 ||
        (newSelectionCount === 1 && currentSelectedIDs[0] !== currentUserOrgId)

      const filters = shouldResetFilters
        ? { organizations, agents: [], tags: [], playbooks: [] }
        : { organizations }
      dispatch(setFilter(filters))
      dispatch(
        selectHierarchyOrganization(newSelectionCount === 1 ? currentSelectedIDs[0] : undefined)
      )
      dispatch(selectHierarchyOrganizations(currentSelectedIDs))
    },
    [allIDs, currentUserOrgId, dispatch]
  )

  useEffect(() => {
    if (!controlsVisible) {
      return undefined
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (dispatchOnClose) {
          dispatchSelections(selectedIDs)
        }
        setControlsVisible(false)
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 50)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [controlsVisible, dispatchOnClose, dispatchSelections, selectedIDs])

  const resetSelectedEntries = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const resetIDs = disabledIDs ?? []
    setSelectedIDs(resetIDs)
    dispatch(setFilter({ organizations: allIDs, agents: [], tags: [], playbooks: [] }))
    dispatch(selectHierarchyOrganization(resetIDs.length === 1 ? resetIDs[0] : undefined))
    dispatch(selectHierarchyOrganizations(resetIDs))
  }

  useEffect(() => {
    dispatch(selectHierarchyOrganizations(initialSelection))
    return () => {
      dispatch(setFilter({ organization: currentUserOrgId, organizations: undefined }))
      dispatch(selectHierarchyOrganization(currentUserOrgId))
    }
  }, [])

  useEffect(() => {
    const allIDsDB = getAllNodeIDs(optionsDB, 'organization_id')
    setAllIDs(allIDsDB)
    dispatch(setFilter({ organizations: allIDsDB }))
  }, [optionsDB])

  const handleNodeClick = (node) => {
    const selectedIDsWithChildren = getNodeWithChildrenIDs(
      node.organization_id,
      optionsDB,
      'organization_id'
    )
    const isSelected = selectedIDs.includes(node.organization_id)
    let newSelectedIDs = []
    if (!isSelected) {
      newSelectedIDs = [...new Set([...selectedIDs, ...selectedIDsWithChildren])]
    } else {
      newSelectedIDs = selectedIDs.filter((id) => !selectedIDsWithChildren.includes(id))
    }
    setSelectedIDs(newSelectedIDs)

    if (!dispatchOnClose) {
      dispatchSelections(newSelectedIDs)
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedQuery(searchQuery), 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const filteredOptions = useMemo(
    () =>
      debouncedQuery
        ? filterHierarchy(optionsDB, debouncedQuery, { keepChildrenOnMatch: true })
        : optionsDB,
    [optionsDB, debouncedQuery]
  )

  useEffect(() => {
    if (!controlsVisible) {
      setSearchQuery('')
      setDebouncedQuery('')
    }
  }, [controlsVisible])

  const selectedCount = selectedIDs.length
  const optionsEmpty = isEmpty(optionsDB)
  const isDisabled = disabled || optionsEmpty
  const chevronClasses = classNames('organization-chevron', { active: controlsVisible })

  return (
    <div
      ref={containerRef}
      data-testid={`nested-select-container-${accessor}`}
      style={{ position: 'relative' }}
    >
      <ReactSelectChevron
        onClick={() => {
          if (!isDisabled) {
            if (controlsVisible && dispatchOnClose) {
              dispatchSelections(selectedIDs)
            }
            setControlsVisible(!controlsVisible)
          }
        }}
        data-testid={`nested-select-chevron-${accessor}`}
        className={chevronClasses}
      />
      {!controlsVisible && (
        <div
          className="placeholder"
          onClick={() => {
            if (!isDisabled) {
              setControlsVisible(true)
            }
          }}
        >
          <div
            data-testid={`organization-select-pill-${accessor}`}
            className="organization-select-pill-ellipsis"
          >
            {(selectedCount === 0 && !initialSelection) || selectedCount === allIDs.length
              ? 'All Organizations'
              : selectedCount === 1
                ? findNodeByValue(optionsDB, selectedIDs[0], 'organization_id')?.name
                : `${selectedCount} selected`}
          </div>
          {selectedCount > 0 && (
            <IconX
              onClick={resetSelectedEntries}
              className="organization-select-clear-x"
              data-testid={`organization-select-clear-x-${accessor}`}
            />
          )}
        </div>
      )}
      {isEmpty(optionsDB) && controlsVisible && (
        <div
          className="organization-select-no-options"
          data-testid={`organization-select-no-options-${accessor}`}
        >
          No Options
        </div>
      )}
      <input
        className="organization-search-input"
        data-testid={`organization-search-input-${accessor}`}
        placeholder={controlsVisible ? 'Search organizations...' : ''}
        onFocus={() => setControlsVisible(true)}
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        disabled={isDisabled}
      />
      {controlsVisible && (
        <div className="organizations-filter">
          <TreeSelect
            data={filteredOptions}
            selectedIDs={selectedIDs}
            disabledIDs={disabledIDs ?? []}
            disabled={isDisabled}
            onNodeClick={handleNodeClick}
            idAccessor="organization_id"
            labelAccessor="name"
            noResultsMessage="No results found"
            noResultsTestId={`organization-search-no-results-${accessor}`}
          />
        </div>
      )}
    </div>
  )
}
